import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Copy, Reply } from 'lucide-react';
import { colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IconButton, OverflowMenu, TextButton, type OverflowMenuAction } from '../../ui';
import { AgentDot } from '../../../AgentDot';
import { agentShortForm } from '../../../../lib/use-agent-name';
import { type AgentSummary } from '../../../../lib/my-agents';
import type { Message } from '../../../../lib/use-chat';
import { messageDir } from '../../../../lib/message-direction';
import { initialsFromName, parseGroupAttribution, resolveGroupMentions } from '../../../../lib/wa-group-message';
import { useTranslation } from '../../../../lib/i18n';
import { MAX_MESSAGE_ATTACHMENTS } from '../../../../lib/attachment-cap';
import { GROUP_AVATAR, authorLabel, eventLine, isFromViewer, participantLabel, personName, resolveAuthor, type GroupSummary } from '../../../../lib/group-chat';
import { GroupParticipantAvatar, groupStyles } from '../../group-chat/GroupAvatars';
import { GroupApprovalCard, MaestroUnavailableLine } from '../../group-chat/GroupMaestro';
import { groupMentionNames, groupSystemLine, maestroAskMentions, mentionMarkdown } from '../../../../lib/group-maestro';
import { groupInfoEventLine, isMyRow, tombstoneKey } from '../../../../lib/group-admin';
import { GroupContentBody } from '../../group-chat/GroupContentBubble';
import { drawsBubble } from '../../../../lib/group-content';
import { GroupForwardedLabel, GroupQuickReactBar, GroupReactionChips, GroupTicks, GroupTimeBadges, type GroupRowExtras } from '../../group-chat/GroupMessageParts';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { AgentJoinCard } from '../../AgentJoinCard';
import { AgentAtWorkCard } from '../../AgentAtWorkCard';
import { CreditGiftCard } from '../../CreditGiftCard';
import { MilestoneCard } from '../../MilestoneCard';
import { MaestroMark } from '../../MaestroMark';
import { MeetingSummaryCard } from '../MeetingSummaryCard';
import { FeedbackReplyCard } from '../FeedbackReplyCard/FeedbackReplyCard';
import { FeedbackInviteCard } from '../FeedbackInviteCard/FeedbackInviteCard';
import { GroupSetupCard } from '../GroupSetupCard/GroupSetupCard';
import { MeetingNotesCard } from '../MeetingNotesCard';
import { MeetingApprovalCard } from '../MeetingApprovalCard';
import type { MeetingApprovalRow } from '../../../../lib/meeting-approval';
import { ProposalsCard } from '../ProposalsCard';
import type { ProposalRow } from '../../../../lib/proposals-card';
import { hexToRgba } from '../../../../lib/hex-to-rgba';
import { SentAttachmentList } from '../attachments';
import { LinkifiedText } from '../../../LinkifiedText';
import { ContactMentionText } from './ContactMentionText';
import { CalendarItemMessage } from './CalendarItemMessage';
import { SiteReviewMessage } from './SiteReviewMessage';
import { EmailReviewMessage } from './EmailReviewMessage';
import { parseSentReview, type SentReviewComment } from '../../../../lib/site-review';
import { parseSentEmailReview, type EmailReviewMode } from '../../../../lib/email-review';
import type { EmailReviewSendResult } from '../../../../lib/email-review-store';
import { parseSentBlogReview } from '../../../../lib/blog-draft-preview';
import type { BlogReviewSendResult } from '../../../../lib/blog-review-store';
import { isDraftActHeld, parseSentDraftReview, type DraftAction, type DraftMessage } from '../../../../lib/draft-preview';
import type { DraftReviewSendResult, DraftSendSource } from '../../../../lib/draft-review-store';
import { useTypewriter } from '../loaders';
import { PACK_REVEAL_CARD_BEAT_MS, PACK_REVEAL_ROW_BEAT_MS, packRevealStep } from '../../../../lib/pack-arrival-reveal';
import styles from './MessageRow.module.css';
import { formatMessageTime, rowGapAfter, TRAILING_BLOCK_GAP, voiceTranscript } from '../message-utils';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { MicGlyph } from './MicGlyph';
import { AssistantMarkdownContent } from './AssistantMarkdownContent';
import { ChoiceChips } from './ChoiceChips';
import { MeetingReconnectButton } from './MeetingReconnectButton';
import { HeldSendChips } from '../../send-hold/HeldSendChips';
import { SurveyWidget } from './SurveyWidget';
import { CreditsCard } from './CreditsCard';
import { InviteCard } from './InviteCard';
import { ChatPackShareCard } from './ChatPackShareCard';
import { ConnectorConnectCard } from './ConnectorConnectCard';
import { ProfileLinkChatCard } from './ProfileLinkChatCard';
import type { ProfileLinkCardData } from '../../../../lib/profile-link-chat-card';
import { ShopifyBatchCard } from './ShopifyBatchCard';
import { ShopifyBriefingCard } from './ShopifyBriefingCard';
import { InsightCard } from './InsightCard';
import { ProductCards } from './ProductCards';
import { attachmentsWithoutProductPictures } from '../../../../lib/product-cards';
import { ConnectorResultCard } from './ConnectorResultCard';
import { OpportunityCard } from './OpportunityCard';
import { OutreachPreviewCard } from './OutreachPreviewCard';
import { SocialPostPreviewCard } from './SocialPostPreviewCard';
import { SocialPostAutoApproveLine } from './SocialPostAutoApproveLine';
import { SocialPostEditFirstChip, SocialPostTaskState } from './SocialPostTaskState';
import { EmailCampaignPreviewCard } from './EmailCampaignPreviewCard';
import { BlogDraftPreviewCard } from './BlogDraftPreviewCard';
import { DraftPreviewCard } from './DraftPreviewCard';
import { ShareOfferCard, ShareResponseRow, ShareSomeoneElse } from './ShareOfferCard';
import { PostConnectedConnectorCard } from './PostConnectedConnectorCard';
import { BandOfferCard } from './BandOfferCard';
import { BandCard } from './BandCard';
import { ChatGoalCard } from './ChatGoalCard';
import { FailedTurnWithHolds } from './FailedTurnWithHolds';
import { OutreachComposerChip } from '../../outreach/OutreachComposerChip';
import { ReactionChips, ReactToggleButton } from './MessageReactions';
import type { ChoiceOption } from '../../../../lib/message-choices';
import {
  AGENT_BUBBLE,
  ASSISTANT_BODY,
  CARD_WRAP,
  SPEAKER_AVATAR,
  SPEAKER_HEADER,
  SPEAKER_NAME,
  SPEAKER_TITLE,
  TIME_BASE,
  USER_BUBBLE,
  USER_BUBBLE_VOICE,
} from './styles';

// WhatsApp quoted-reply: a quiet quote block above the bubble body. `sender`/`preview` are
// resolved server-side (digest-engine); an unresolved reference renders the generic line.
function ReplyQuote({ replyTo }: { replyTo: NonNullable<Message['replyTo']> }) {
  const { t } = useTranslation();
  return (
    <div style={REPLY_QUOTE}>
      {replyTo.sender && <div style={REPLY_QUOTE_SENDER}>{replyTo.sender}</div>}
      <div dir="auto" style={REPLY_QUOTE_TEXT}>
        {replyTo.preview || t('chat.replyTo.generic')}
      </div>
    </div>
  );
}

const REPLY_QUOTE: React.CSSProperties = {
  borderInlineStart: '3px solid rgba(var(--ink-rgb),0.25)',
  padding: '4px 10px',
  marginBottom: 6,
  borderRadius: 6,
  background: 'rgba(var(--ink-rgb),0.05)',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
};

const REPLY_QUOTE_SENDER: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(var(--ink-rgb),0.75)',
};

const REPLY_QUOTE_TEXT: React.CSSProperties = {
  fontSize: '0.78125rem',
  color: 'rgba(var(--ink-rgb),0.55)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Bounded by the bubble (whose max-width is container-capped), NOT a fixed px width — a fixed
  // cap wider than a narrow panel forced the bubble past its container and clipped (RTL panel bug).
  maxWidth: '100%',
};

function ReplyActionButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton size="small" variant="text" onClick={onClick} aria-label={t('chat.reply.action')} title={t('chat.reply.action')}>
      <Reply size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

// Copy the message body — an inline toolbar button beside Reply/React (Figma 14692:215825), not a menu
// item. Same ghost skin + hover as the reaction button. Carries the copy behavior the old
// MessageActionsMenu held: clipboard write + the copy_message BI beacon.
function CopyActionButton({ copySource }: { copySource: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={() => {
        logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'copy_message' });
        navigator.clipboard.writeText(copySource).catch(() => {
          // Clipboard is gated by focus/permission; nothing actionable if it's denied.
        });
      }}
      aria-label={t('common.copy')}
      title={t('common.copy')}
    >
      <Copy size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

/** Phase 10a: a group row's ⋯ menu — Delete for everyone / Report message, each item decided by the panel. */
function GroupRowMenu({ actions }: { actions: OverflowMenuAction[] }) {
  const { t } = useTranslation();
  return <OverflowMenu actions={actions} ariaLabel={t('chat.moreActions')} triggerVariant="text" />;
}

const EMPTY_SELECTED_IDS: ReadonlySet<string> = new Set();

export function MessageRow({
  message,
  agentCardRun,
  tightenGapAfter,
  agent,
  maestroName,
  brief,
  isMaestro,
  briefParticipantIds,
  isSummaryTurn,
  conversationId,
  threadConversationId,
  canSendToChat,
  isWaGroup,
  assistantName,
  participantsByWaId,
  groupChat,
  onRetryGroupPost,
  groupMenuActions,
  groupRow,
  onReply,
  onToggleReaction,
  selectedChoiceIds,
  emailReviewMode,
  emailCardLatest,
  blogCardLatest,
  draftCardLatest,
  cardReviewComments,
  onSendEmailReview,
  onSendBlogReview,
  onSendDraftReview,
  onSelectChoice,
  onPickBand,
  onShareSomeoneElse,
  onShareSomeoneElseOpened,
  shareSomeoneElseDisabled,
  onRequestConnectorLink,
  onSendSocialPostTap,
  onKickOffGoal,
  onBuyProduct,
  onRequestDesignReview,
  shopifyBatchActionable,
  shopifyBriefingActionable,
  onConnectorConnected,
  profileLinkCardReadOnly,
  latestProfileLinkCard,
  onReopenSurvey,
  alignEdges,
  turnActive,
  meetingApprovalRow,
  proposalRows,
  onRetryFailedTurn,
  revealStaged,
  revealDone,
  onRevealed,
  revealProgress,
  hideToolbar,
  stampDate,
}: {
  message: Message;
  agentCardRun?: NonNullable<Message['agentCard']>[];
  // This row continues a same-speaker burst — the NEXT message is another plain
  // reply from the same agent with no user turn between them — so the gap below
  // this row tightens instead of using full turn-taking spacing.
  tightenGapAfter?: boolean;
  agent?: AgentSummary;
  maestroName?: string;
  // This row is inside a brief round-table. Gates the brief-only chrome — a sub-agent's turn gets a
  // bordered bubble in its identity colour, the Maestro's turn wears the Maestro mark — matching native
  // (which draws none of this in a normal 1:1 chat even if an `agentId` is present).
  brief?: boolean;
  // This turn is the Maestro/facilitator's, even if it carries an `agentId` (the round-table's framing
  // turns do, but the id isn't a hired agent). Forces the Maestro treatment — mark + plain prose — over
  // the sub-agent bubble. Resolved by the panel against the agent roster.
  isMaestro?: boolean;
  // Distinct agent ids who spoke in this brief, first-spoke order — the MeetingSummaryCard's avatar
  // cluster + "N agents met" count.
  briefParticipantIds?: string[];
  // This row is the round-table's wrap-up — render the MeetingSummaryCard. Resolved by the panel from
  // the server flag OR the brief fetch's raw metadata (the messages API proxies to the pod in dev).
  isSummaryTurn?: boolean;
  // The thread this row belongs to. Only the summary card reads it — the questions the meeting raised
  // hang off the conversation, and answering them there is what stops the next brief re-asking.
  /** The thread this row belongs to, always set: what the attachments settle a rendering image's game against. */
  threadConversationId?: string;
  conversationId?: string;
  /** Whether a send into this thread can actually happen NOW — false while a turn is streaming, which
   *  `send` refuses. Only the meeting-notes card reads it, to disable its task actions rather than let a
   *  press look like it worked. Absent means yes. */
  canSendToChat?: boolean;
  // WhatsApp GROUP conversation: inbound member turns carry a `[Name (waId)]:`
  // attribution prefix that we decode into a sender avatar + name (display-only).
  isWaGroup?: boolean;
  /**
   * A Symphony GROUP chat (behind `symphonyGroupChat`): its summary, and whether this row heads a run of messages from
   * one author (`authorRunHeads`) — only a head shows the author's avatar and name. Absent on every private chat.
   */
  groupChat?: { summary: GroupSummary; head: boolean };
  assistantName?: string;
  participantsByWaId?: Map<string, string>;
  /** A group post of the viewer's that failed: tap to post it again under the SAME clientMessageId. */
  onRetryGroupPost?: () => void;
  /** Phase 10a: the row's ⋯ menu in a live group (Delete for everyone / Report message), decided by the panel. */
  groupMenuActions?: OverflowMenuAction[];
  /** Phase 10b: the group row's reactions, labels and quick reaction row, decided by the panel. */
  groupRow?: GroupRowExtras;
  // Start a quoted reply to this message — shown as a hover action on user/assistant rows
  // (and WA-group member rows) when the surface supports composing.
  onReply?: (message: Message) => void;
  // Toggle the user's own emoji reaction on this message (quick bar + chips).
  // NOT gated on locked channels — reactions are in-app annotations.
  onToggleReaction?: (message: Message, emoji: string) => void;
  // Option ids already answered for this row's structured ask (derived from the
  // transcript's USER choiceAnswer rows) — those chips render selected.
  selectedChoiceIds?: ReadonlySet<string>;
  emailReviewMode?: EmailReviewMode;
  emailCardLatest?: boolean;
  blogCardLatest?: boolean;
  draftCardLatest?: boolean;
  cardReviewComments?: readonly SentReviewComment[];
  onSendEmailReview?: (message: Message, text: string) => EmailReviewSendResult;
  onSendBlogReview?: (message: Message, text: string) => BlogReviewSendResult;
  onSendDraftReview?: (message: Message, text: string, source?: DraftSendSource, draftAction?: DraftAction) => DraftReviewSendResult;
  // Tap a choice chip → send the option's label as a normal user turn carrying
  // the structured answer. Absent (read-only surface) => inert chips.
  onSelectChoice?: (message: Message, option: ChoiceOption) => void;
  /** "Send to someone else" on a share offer: the person the user typed. Absent on a channel-locked
   *  thread, same as `onSelectChoice`. Returns whether the send was taken, so the field only closes on one. */
  onShareSomeoneElse?: (message: Message, recipient: { email: string }) => boolean;
  /** The share offer's email field was revealed. */
  onShareSomeoneElseOpened?: (message: Message) => void;
  /** A reply is streaming: the control is shown but not actionable (see `ShareSomeoneElse`). */
  shareSomeoneElseDisabled?: boolean;
  /** Tap a band on the offer card. Absent on a channel-locked thread, same as `onSelectChoice`. */
  /** Returns whether the pick was taken; the card opens the band's connect walk only when it was. */
  onPickBand?: (option: { id: string; name: string }) => boolean;
  // "Get a new link" on an expired connector card → send the localized ask as a
  // normal user turn. Absent (read-only surface) => the card shows the line only.
  onRequestConnectorLink?: (message: Message, text: string) => void;
  onSendSocialPostTap?: (message: Message, tap: DraftMessage) => boolean;
  // "Buy it for me" on a product card → send its line as a normal user turn. Absent (read-only surface) => no buy button.
  onBuyProduct?: (message: Message, text: string) => void;
  // "Kick off" on a goal card → send its localized line as a normal user turn, exactly as if typed.
  // Absent (read-only surface) => the card shows no button.
  onKickOffGoal?: (message: Message, text: string) => void;
  // "Get a design review" under a Wixel asset this row attached → send the request into THIS conversation as a
  // normal user turn (lib/wixel-chat-asset.ts). Absent (read-only surface) => the row offers Edit in Wixel only.
  onRequestDesignReview?: (message: Message, text: string) => void;
  shopifyBatchActionable?: boolean;
  shopifyBriefingActionable?: boolean;
  // The card observed its connect: the genome is about to wake the loop, so the transcript shows a
  // thinking line until the harness sends the real one (see lib/connect-handoff).
  onConnectorConnected?: (name: string) => void;
  /** A channel-locked thread: the profile-link card shows its state without the owner's Edit. */
  profileLinkCardReadOnly?: boolean;
  /** The conversation's LATEST card for this row's link (latest card wins) — drawn instead of the row's own. */
  latestProfileLinkCard?: ProfileLinkCardData;
  // Take back an ask the reader closed with the X: the prompts below the reply become the way back
  // into the card. Passed ONLY for the row whose ask is still the current one (nothing said past it),
  // because a row that cannot reopen anything must not read as clickable — see `reopenableSurveyAskId`
  // in MessagesPanel.
  onReopenSurvey?: () => void;
  // Full-screen desktop chat: inset the whole row (user bubble + assistant message) by
  // ROW_EDGE_INSET from the reading column's edges, so both land the same distance from it
  // (narrow/docked keep no inset — a slight zig-zag, by design there).
  alignEdges?: boolean;
  // This row is the one currently being written: the panel's own SSE is open AND this is the newest
  // row. ANDed with the row's `streamOpen` to decide `isCursorOn`, as the safety net for a stream cut
  // without a `messageEnd` — that row keeps `streamOpen` until the next block opens, and on its own
  // would read as still streaming the next time anything happened in the conversation.
  turnActive?: boolean;
  /** The live decision behind this row's `meetingApproval` marker, once the panel has read it. Absent
   *  until then, and for every other row — the marker alone never renders buttons. */
  meetingApprovalRow?: MeetingApprovalRow;
  /** The live proposals behind this row's `proposalsCard` marker, in marker order. Absent until read,
   *  and when none matched — the marker alone never renders checkboxes. */
  proposalRows?: ProposalRow[];
  /** "Try again" on a failed-turn row. Passed ONLY for the newest failure with nothing said since, on a
   *  thread that accepts sends — see `failedTurnRetryTarget` in lib/failed-turn.ts. */
  onRetryFailedTurn?: () => void;
  /** This row is part of a pack walkthrough that types itself in (lib/pack-arrival-reveal). Read at mount. */
  revealStaged?: boolean;
  /** The walkthrough was cut short (the reader wrote): a staged row shows its full words at once. */
  revealDone?: boolean;
  /** A staged row has finished typing and the next one may start. */
  onRevealed?: (id: string) => void;
  /** How far staged rows have typed, kept by the panel so a remounted row resumes (lib/use-pack-arrival-reveal). */
  revealProgress?: Map<string, number>;
  /** A row inside a composed message that is not its last row: the last row draws the one toolbar
   *  (lib/pack-arrival-message). */
  hideToolbar?: boolean;
  /** The time the toolbar shows in place of the row's own `createdDate` (lib/pack-arrival-message). */
  stampDate?: string;
}) {
  const { t, i18n } = useTranslation();
  const isUser = message.role === 'USER';
  // Both assistant-side roles. An `ASSISTANT_STEP` row — prose the agent wrote in the same step it
  // called a tool — is a message the reader already watched stream in, so it renders as the bubble it
  // was, with the same timestamp, copy button and reply affordance as any other agent turn. (It was
  // `ASSISTANT` only until #7461 hid it; that was reversed 2026-08-27 — see `isVisibleMessage`.)
  // `THINKING` is NOT here: tool-authored status never reaches a row.
  const isAssistant = message.role === 'ASSISTANT' || message.role === 'ASSISTANT_STEP';
  // Per-thread dismissal of the credits card. Local rather than persisted: the card lives in the
  // transcript, so "not now" hides THAT card rather than silencing a global prompt.
  const [creditsCardDismissed, setCreditsCardDismissed] = useState(false);
  // The quick-react emoji bar toggles open under the tool bar when the smiley is clicked
  // (Figma 14511:160345 "When Click on Emoji icon"); picking an emoji closes it.
  const [reactOpen, setReactOpen] = useState(false);
  const rawContent = message.content || '';
  const transcript = voiceTranscript(rawContent);
  const isVoice = transcript !== null;
  const content = transcript ?? rawContent;
  // A voice note carries its clip as an audio attachment — the player renders
  // ABOVE the (dimmed) transcript and replaces the gutter mic marker, which
  // stays only for player-less voice-call turns.
  const hasAudioAttachment = !!message.attachments?.some((a) => isAudioAttachment(a.mimeType));
  // A product card draws its own picture, so that attachment is not drawn again as a file.
  const fileAttachments = attachmentsWithoutProductPictures(message.attachments ?? [], isAssistant ? message.productCards : undefined);
  // Hooks must run on EVERY render, before the card/group early returns below —
  // `isWaGroup` settles async (the conversations query), so a row can re-render
  // from the plain branch into the group branch; a hook below a conditional
  // return would then change the hook count and crash the tree (React #300).
  // A staged row fades in with its card first, then types its words at a steady pace (see
  // `lib/pack-arrival-reveal`). Both are captured at mount: a pushed row arrives with `animate` and the
  // history reload ~600ms later replaces it with the same-id server row, which has none. Read live, that
  // flip cut the reveal short mid-reply (`useTypewriter` now also jumps to the end on such a flip).
  const [animated] = useState(!!message.animate);
  const [staged] = useState(!!revealStaged && !isUser);
  const [cardAbove] = useState(() => hasCardAboveWords(message));
  const [pace] = useState(() => {
    if (!staged) return undefined;
    const startAt = (message.id && revealProgress?.get(message.id)) || 0;
    // A row resuming after a remount has already shown its card: no second beat before the words.
    return { startDelayMs: cardAbove && !startAt ? PACK_REVEAL_CARD_BEAT_MS : 0, step: packRevealStep(content.length), startAt };
  });
  const displayed = useTypewriter(content, isUser || !(animated || staged), pace, staged && !!revealDone);
  const typed = displayed.length >= content.length;
  if (staged && message.id && revealProgress) revealProgress.set(message.id, displayed.length);
  const reportRevealed = staged && typed && !!message.id ? onRevealed : undefined;
  useEffect(() => {
    if (!reportRevealed || !message.id) return undefined;
    const id = message.id;
    const timer = setTimeout(() => reportRevealed(id), PACK_REVEAL_ROW_BEAT_MS);
    const cancel = () => clearTimeout(timer);
    return cancel;
  }, [reportRevealed, message.id]);
  // What sits UNDER the words (chips, connect and goal cards, the tool bar) waits for them on a staged row.
  const holdBelow = staged && !typed;
  const Below = staged ? ArriveGroup : Fragment;
  // The FIRST message of a run of consecutive agentCard messages carries the
  // whole run's agents; the card self-plays once for the whole group.
  if (message.agentCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <AgentJoinCard agents={agentCardRun?.length ? agentCardRun : [message.agentCard]} />
      </div>
    );
  }
  // The live at-work card. One card per row, with NO run consolidation — the deliberate opposite of
  // the join card above. Consecutive `agentCard` rows are one hiring moment and fold into a single
  // orbit; consecutive at-work rows are DIFFERENT TASKS, and folding them would hide every task but one.
  // A turn that failed: the harness's canned English apology (`metadata.systemError`) is replaced by the
  // failed-turn row, so the reader can tell the turn died and retry it in one tap.
  if (message.systemError && isAssistant) {
    // A turn can fail AFTER it left a message waiting: the hold is real, so its Cancel stays under the failed row.
    // A send the server refused for its attachment count says so (lib/attachment-cap.ts).
    const line = message.sendRefusal === 'too_many_attachments' ? t('chat.attachments.limit', { max: MAX_MESSAGE_ATTACHMENTS }) : undefined;
    return <FailedTurnWithHolds messageId={message.id} onRetry={onRetryFailedTurn} sendHoldCard={message.sendHoldCard} conversationId={conversationId} line={line} />;
  }
  if (message.agentAtWorkCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <AgentAtWorkCard card={message.agentAtWorkCard} />
      </div>
    );
  }
  if (message.milestoneCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MilestoneCard card={message.milestoneCard} />
      </div>
    );
  }
  if (message.creditGiftCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <CreditGiftCard card={message.creditGiftCard} />
      </div>
    );
  }
  // A recipient's reaction renders as its own row IN PLACE OF a bubble, the same way the meeting
  // summary below does. The row's `content` carries the same sentence, deliberately: a build that has
  // never heard of this key then shows the reaction as a plain assistant line instead of nothing at
  // all — and a build that HAS heard of it must not print the sentence twice, which is what replacing
  // the bubble buys. There is no agent turn behind a bare emoji, so this is not a reply and must not
  // read as one.
  if (message.shareResponseCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <ShareResponseRow row={message.shareResponseCard} />
      </div>
    );
  }
  // The brief round-table's wrap-up turn renders as the meeting summary card in place of a bubble
  // (matching iOS/Android). The avatar cluster + count come from the thread's participants.
  if (message.agentMeetingSummary || isSummaryTurn) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MeetingSummaryCard
          content={content}
          createdDate={message.createdDate}
          agentIds={briefParticipantIds ?? []}
          conversationId={conversationId}
        />
        {/* The summary row can carry an ordinary structured ask (any `choices` block the producer
            attaches). The card replaces the bubble, not the row's choices — draw them the same
            way the bubble path does below, so a tap sends the ordinary choiceAnswer. Mirrors iOS/Android. */}
        {message.choices && (
          <ChoiceChips
            choices={message.choices}
            selectedIds={selectedChoiceIds ?? EMPTY_SELECTED_IDS}
            onSelect={onSelectChoice ? (option) => onSelectChoice(message, option) : undefined}
          />
        )}
      </div>
    );
  }
  // A meeting Maestro sat in: the write-up row renders as the notes card, drawn from the
  // `notes.md` attached to it. Its files still list in the chat's Files panel; the card is the row.
  // A feedback-round answer: the design, who answered and what they said, as a card instead of the raw line.
  // A feedback round's invitation to this user: the design and the answer box, answered in the chat (the row's text,
  // with its link, is what an older client shows).
  if (message.feedbackInviteCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <FeedbackInviteCard card={message.feedbackInviteCard} conversationId={conversationId} />
      </div>
    );
  }
  // The Maestro's group-setup card: drawn in place of the row's fallback line ("Group setup: Love Qa"), the setup
  // before the group exists and the group's live status card after (lib/group-setup-card.ts).
  if (message.groupSetupCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <GroupSetupCard card={message.groupSetupCard} conversationId={conversationId ?? message.conversationId} message={message} />
      </div>
    );
  }
  if (message.feedbackReplyCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <FeedbackReplyCard card={message.feedbackReplyCard} conversationId={conversationId} canSend={canSendToChat !== false} />
      </div>
    );
  }
  if (message.meetingNotesCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MeetingNotesCard message={message} conversationId={conversationId} canSend={canSendToChat !== false} />
      </div>
    );
  }
  const toggleReaction = onToggleReaction ? (emoji: string) => onToggleReaction(message, emoji) : undefined;
  // The tool bar's floating picker (`ReactionPickerAnchor`) serves a 1:1 row. A group row keeps its own
  // quick-react bar under the tool bar, so it gets no picker.
  const pickReaction =
    toggleReaction && !groupRow?.onReact
      ? (emoji: string) => {
          toggleReaction(emoji);
          setReactOpen(false);
        }
      : undefined;
  const closeReact = () => setReactOpen(false);
  // WhatsApp GROUP member turn: decode the server's `[Name (waId)]:` attribution
  // into a group-chat row — sender avatar + name, body with mentions resolved,
  // the prefix hidden. Gated on `isWaGroup` so 1:1 rendering is never touched.
  // A Symphony group chat's membership event (join, leave, Maestro added/removed): a centred system line.
  // ENDED also draws on the owner's chat after it ended — private again, so no summary (it names itself).
  if (message.groupEvent && (groupChat || message.groupEvent.type === 'ENDED')) {
    return (
      <div data-msg-id={message.id} data-testid="group-event-line" className={groupStyles.eventLine} style={{ display: 'flex', justifyContent: 'center' }}>
        {eventLine(message.groupEvent, groupChat?.summary ?? null, t)}
      </div>
    );
  }
  // Phase 10a: a SYSTEM line for the group's name / description / photo / settings, or someone joining by link — the
  // localized line with the actor's first name, else the row's own text (never empty, never a raw key).
  const infoLine = groupChat && message.groupMeta?.infoEvent ? groupInfoEventLine(message.groupMeta.infoEvent, message.content, groupChat.summary, t) : null;
  if (infoLine) {
    return (
      <div data-msg-id={message.id} data-testid="group-info-event-line" className={groupStyles.eventLine} style={{ display: 'flex', justifyContent: 'center' }}>
        {infoLine}
      </div>
    );
  }
  // An unknown kind with no text of its own draws nothing — never a bubble, never a raw key.
  if (groupChat && message.groupMeta?.infoEvent) return null;
  // Phase 10a: a message deleted for everyone — a muted italic bubble on the same side, under the same author, with
  // no attachments, no quote and no actions.
  if (groupChat && message.groupMeta?.deleted) {
    const mine = isMyRow(message, groupChat.summary.viewerId) && message.author?.kind !== 'MAESTRO';
    const who = !mine && groupChat.head ? resolveAuthor(groupChat.summary, message.author ?? null) : null;
    return (
      <div data-msg-id={message.id} className="orion-msg-row" style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
        {who && (
          <div style={SPEAKER_HEADER} data-testid="group-author-header">
            <GroupParticipantAvatar participant={who} size={GROUP_AVATAR.message.size} />
            <span style={SPEAKER_NAME}>{authorLabel(who, t)}</span>
          </div>
        )}
        <div data-testid="group-tombstone" style={{ ...USER_BUBBLE, fontStyle: 'italic', color: 'var(--ds-text-secondary)' }}>
          {t(tombstoneKey(message.groupMeta.deleted, groupChat.summary.viewerId))}
        </div>
        {/* Phase 10b: a tombstone offers only "Delete for me". */}
        {groupMenuActions?.length ? <div style={{ marginTop: 4 }}><GroupRowMenu actions={groupMenuActions} /></div> : null}
      </div>
    );
  }
  // Phase 8: a SYSTEM line — a Maestro's work was stopped, or a chain of Maestro work paused at a bound (R5: people
  // were added). Centred like the membership lines, always localized; never a Maestro bubble.
  const systemLine = groupChat ? groupSystemLine(message.groupMeta, message.content, groupChat.summary, t, t('home.map.maestro'), i18n?.language) : null;
  if (systemLine) {
    return (
      <div data-msg-id={message.id} data-testid="group-system-line" className={groupStyles.eventLine} style={{ display: 'flex', justifyContent: 'center' }}>
        {systemLine}
      </div>
    );
  }
  // R5: an "added" SYSTEM row nobody can be named in, with no text of its own, draws nothing — never a bubble.
  if (groupChat && message.groupMeta?.membersAdded) return null;
  // Phase 5: a Maestro's "can't help right now" notice — a centred line like the membership lines; its `content` is
  // ignored, the line is always localized.
  if (groupChat && message.maestroUnavailable) {
    return <MaestroUnavailableLine id={message.id} reason={message.maestroUnavailable} summary={groupChat.summary} author={message.author} />;
  }
  // Phase 5: a Maestro's public approval card, in place of its bubble, under that Maestro's author line (once per run).
  if (groupChat && message.groupApproval && message.author?.kind === 'MAESTRO') {
    const approvalMaestro = resolveAuthor(groupChat.summary, message.author);
    return (
      <div data-msg-id={message.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 16 }}>
        {groupChat.head && approvalMaestro && (
          <div style={SPEAKER_HEADER} data-testid="group-author-header">
            <GroupParticipantAvatar participant={approvalMaestro} size={GROUP_AVATAR.message.size} />
            <span style={SPEAKER_NAME}>{participantLabel(approvalMaestro, t)}</span>
          </div>
        )}
        <GroupApprovalCard card={message.groupApproval} summary={groupChat.summary} conversationId={message.conversationId ?? conversationId ?? null} />
      </div>
    );
  }
  // A Symphony GROUP chat: another member's turn is drawn exactly like a WhatsApp group member's — the author's
  // avatar and name over a leading bubble — except that the author comes from the server-stamped `author` rather
  // than a text prefix, and the avatar + name show once per run of that author's messages.
  const groupOther = groupChat && isUser && message.author && !isFromViewer(groupChat.summary, message) ? message.author : null;
  const groupParticipant = groupOther
    // Someone the summary no longer holds (they left): named by the fallback, never a blank line.
    ? resolveAuthor(groupChat!.summary, groupOther) ?? { id: groupOther.principalMetaSiteId, kind: 'HUMAN' as const, displayName: personName('', 'MEMBER', t), role: 'MEMBER' as const }
    : null;
  const groupAttribution = isWaGroup
    ? parseGroupAttribution(message.content)
    : groupParticipant
      ? { displayName: authorLabel(groupParticipant, t), waId: groupParticipant.id, isOwner: false, text: message.content ?? '' }
      : null;
  // Phase 10c: a typedContent group message (photo, poll, place, …) draws its own body in place of the text; a sticker, no bubble.
  const typedContent = groupChat && !message.groupMeta?.deleted ? message.groupMeta?.content : undefined;
  const typedBody = (authorName: string) =>
    typedContent ? (
      <GroupContentBody message={message} content={typedContent} conversationId={message.conversationId ?? conversationId ?? null} summary={groupChat?.summary ?? null} viewerId={groupChat?.summary.viewerId} authorName={authorName} />
    ) : null;
  const typedBubble = (base: React.CSSProperties): React.CSSProperties => (typedContent && !drawsBubble(typedContent) ? { ...base, background: 'transparent', border: 0, boxShadow: 'none', padding: 0 } : base);
  if (groupAttribution) {
    const showSpeaker = !groupParticipant || groupChat!.head;
    const time = formatMessageTime(message.createdDate);
    const resolvedText = groupParticipant ? groupAttribution.text : resolveGroupMentions(groupAttribution.text, {
      assistantName: assistantName || t('chat.agentFallback'),
      participantsByWaId,
    });
    // A group member's voice note arrives voice-wrapped too — same display
    // rule: player (attachment) above, bare transcript below, dimmed.
    const groupTranscript = voiceTranscript(resolvedText);
    const text = groupTranscript ?? resolvedText;
    return (
      <div
        data-msg-id={message.id}
        className="orion-msg-row"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 24 }}
      >
        {showSpeaker && (
          <div style={SPEAKER_HEADER} data-testid={groupParticipant ? 'group-author-header' : undefined}>
            {groupParticipant ? (
              <GroupParticipantAvatar participant={groupParticipant} size={GROUP_AVATAR.message.size} />
            ) : (
              <span aria-hidden style={{ ...SPEAKER_AVATAR, background: colorForAgent(groupAttribution.waId) }}>
                {initialsFromName(groupAttribution.displayName)}
              </span>
            )}
            <span style={SPEAKER_NAME}>{groupAttribution.displayName}</span>
          </div>
        )}
        <div style={{ display: 'flex', width: '100%', maxWidth: '100%' }}>
          <div style={typedBubble(USER_BUBBLE)}>
            {groupRow?.forwarded && <GroupForwardedLabel />}
            {message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
            {groupTranscript !== null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            {typedContent ? typedBody(groupAttribution.displayName) : (
              <span
                dir="auto"
                style={{ whiteSpace: 'pre-wrap', display: 'block', ...(groupTranscript !== null ? { opacity: 0.72, marginTop: 8 } : {}) }}
              >
                <LinkifiedText text={text} />
              </span>
            )}
            {groupTranscript === null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            {time && <div style={{ ...TIME_BASE, color: 'var(--ds-text-secondary)' }}>{groupRow && <GroupTimeBadges edited={groupRow.edited} starred={groupRow.starred} />}{time}</div>}
          </div>
        </div>
        <ReactionChips reactions={message.reactions} onToggle={toggleReaction} overlapBubble />
        {groupRow && <GroupReactionChips chips={groupRow.chips} summary={groupRow.summary} onChip={groupRow.onChip} overlapBubble />}
        {(toggleReaction || onReply || groupMenuActions?.length || groupRow?.onReact) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
            {(toggleReaction || groupRow?.onReact) && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} onPick={pickReaction} onClose={closeReact} />}
            {groupMenuActions?.length ? <GroupRowMenu actions={groupMenuActions} /> : null}
          </div>
        )}
        {groupRow?.onReact && reactOpen && (
          <div style={{ marginTop: 12 }}>
            <GroupQuickReactBar mine={groupRow.mine} onPick={(emoji) => { groupRow.onReact?.(emoji); setReactOpen(false); }}
              onMore={groupRow.onMoreReactions ? () => { groupRow.onMoreReactions?.(); setReactOpen(false); } : undefined} />
          </div>
        )}
      </div>
    );
  }
  const time = formatMessageTime(stampDate ?? message.createdDate);
  // The caret marks "the reply is still being written", which is TWO things: the server is still
  // appending to this row (`streamOpen`), or the typewriter has not finished revealing what already
  // arrived. It used to be the second alone — and that one flaps, because the typewriter (212-515
  // chars/s) outruns the SSE feed and catches up between almost every delta. So the caret mounted and
  // unmounted tens of times a second, each toggle adding/removing a 24px line box under the prose
  // (see `AssistantMarkdownContent`) and jerking the whole bottom-pinned transcript by that much —
  // measured on the reported recording as nine one-frame ±23px jumps of the entire chat. `streamOpen`
  // holds steady for the row's whole stream, so the caret now mounts once and unmounts once.
  const isCursorOn = !isUser && (animated || staged) && ((!!message.streamOpen && !!turnActive) || displayed.length < content.length);
  // Message-level copy sits in the "more" menu — suppressed while streaming and when the body has
  // code fences (they carry their own per-block copy).
  const showCopy = isAssistant && !isVoice && !isCursorOn && !content.includes('```');
  // A sub-agent's round-table turn (e.g. a brief) is attributed to that agent;
  // color is always by id, name/title from the roster. Absent => the Maestro's
  // turn, which is ALSO labeled when `maestroName` is set (brief group-chat).
  const attributedAgentId = isAssistant && !isMaestro ? message.agentId : undefined;
  // A Maestro's turn in a Symphony group chat: labelled "{agentName} · {ownerFirstName}'s assistant" under its own
  // MaestroMark, once per run — two Maestros can share a name, never their owner.
  const groupMaestro = groupChat && isAssistant && message.author?.kind === 'MAESTRO' ? resolveAuthor(groupChat.summary, message.author) : null;
  const speaker = groupMaestro
    ? (groupChat!.head ? { color: 'var(--accent-solid)', name: participantLabel(groupMaestro, t), title: undefined, initial: undefined } : undefined)
    : attributedAgentId
    ? { color: colorForAgent(attributedAgentId), name: agent?.name || t('chat.agentFallback'), title: agent?.title, initial: agent?.name?.[0] }
    : isAssistant && maestroName
    ? { color: 'var(--accent-solid)', name: maestroName, title: undefined, initial: agentShortForm(maestroName)[0] }
    : undefined;
  // Whether this row DRAWS a bubble (the same branch the body style takes below) — a user turn, or a
  // sub-agent's brief turn. Assistant prose flows bare on the surface, so it has no edge for the
  // reaction pill to sit on.
  const hasBubble = isUser || !!(brief && attributedAgentId);
  // A sent site review (contract §2.5) reads as a card; anything that is not exactly that format stays plain text.
  const sentReview = isUser && !isVoice ? parseSentReview(content) : null;
  const sentEmailReview = isUser && !isVoice && !sentReview ? parseSentEmailReview(content) : null;
  const sentBlogReview = isUser && !isVoice && !sentReview && !sentEmailReview ? parseSentBlogReview(content) : null;
  const sentDraftReview = isUser && !isVoice && !sentReview && !sentEmailReview && !sentBlogReview ? parseSentDraftReview(content) : null;
  // What this row draws BELOW its bubble — the chips, and either a brief's bare timestamp or the
  // reply/copy/react tool bar. A row carrying one keeps the full turn gap; see `rowGapAfter`.
  const hasReactionChips = (message.reactions?.length ?? 0) > 0 || (groupRow?.chips.length ?? 0) > 0;
  const hasToolbar = isAssistant && !hideToolbar && (brief ? !!time : !!(time || showCopy || onReply || toggleReaction));
  return (
    <div
      data-msg-id={message.id}
      // The sent text alone, without the row's chrome (a timestamp appears once the row is persisted), so
      // the scroll hold can tell the same message under a new id (`use-scroll.ts`, `holdSendRow`).
      data-msg-text={isUser ? message.content : undefined}
      className={[isAssistant || isUser ? 'orion-msg-row' : '', staged && !pace?.startAt ? styles.arrive : ''].filter(Boolean).join(' ') || undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: rowGapAfter(!!tightenGapAfter, hasReactionChips || hasToolbar),
        // A group post on its way: the bubble is there at once, dimmed until the server has it.
        ...(message.groupPost?.state === 'sending' ? { opacity: 0.6 } : {}),
      }}
    >
      {speaker && (
        // Brief turns sit 12 below the speaker label (Figma "Morning Brief" itemSpacing 12); the
        // narrower non-brief speaker label keeps its 6.
        <div style={brief ? { ...SPEAKER_HEADER, marginBottom: 12 } : SPEAKER_HEADER}>
          {/* A sub-agent wears its bare multi-band identity dot (native's `AgentMark` — no letter
              overlaid); the Maestro wears its reserved brand mark in a brief (matching native), else
              the solid `--accent-solid` circle with its initial. */}
          {groupMaestro ? (
            <GroupParticipantAvatar participant={groupMaestro} size={GROUP_AVATAR.message.size} />
          ) : attributedAgentId ? (
            <AgentDot id={attributedAgentId} size={brief ? BRIEF_AVATAR : (SPEAKER_AVATAR.width as number)} />
          ) : brief ? (
            <MaestroMark size={BRIEF_AVATAR} />
          ) : (
            <span aria-hidden style={{ ...SPEAKER_AVATAR, background: speaker.color }}>
              {(speaker.initial ?? '·').toUpperCase()}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <span style={SPEAKER_NAME}>{speaker.name}</span>
            {/* "Name • Role" — the bullet + role in the secondary ink (Figma "Morning Brief"). */}
            {speaker.title && (
              <span style={SPEAKER_TITLE}>
                <span aria-hidden style={{ marginInlineEnd: 4 }}>
                  •
                </span>
                {speaker.title}
              </span>
            )}
          </span>
        </div>
      )}
      {/* Every card a reply rides sits ABOVE the reply's own words: the card is the thing being
          shown, and the words are the agent asking about it. Reading order is card, then question,
          then the choice chips that answer it — so the chips stay BELOW the text, still under their
          own card. The connect card is the exception: it renders under the words (see below). */}
      {isAssistant && message.insightCard && <InsightCard card={message.insightCard} conversationId={conversationId} />}
      {isAssistant && message.connectorResultCard && <ConnectorResultCard card={message.connectorResultCard} conversationId={conversationId} />}
      {isAssistant && message.opportunityCard && <OpportunityCard card={message.opportunityCard} />}
      {/* Two whole bands to choose between. Content only — the pick is this row's own chips below, which
          is why nothing here is tappable. */}
      {isAssistant && message.bandOfferCard && (
        <BandOfferCard card={message.bandOfferCard} conversationId={conversationId} onPick={onPickBand} />
      )}
      {/* The band as created: the team, and one collapsed row per app that opens onto its routines. One
          card in place of the N connector cards the connect chain would otherwise stack under this reply. */}
      {isAssistant && message.bandCard && <BandCard card={message.bandCard} conversationId={conversationId} onConnected={onConnectorConnected} />}
      {/* What a connected app brought them, with a switch per automation. Below the band card, so the
          team comes first and the routines it runs follow. The reply's own words still say what the
          connection brought, because a client that cannot render this shows only those words. */}
      {isAssistant && message.shopifyBatchCard && <ShopifyBatchCard key={message.shopifyBatchCard.batchId} card={message.shopifyBatchCard}
        disabled={canSendToChat === false || shopifyBatchActionable !== true}
        onSend={onKickOffGoal ? text => onKickOffGoal(message, text) : undefined} />}
      {isAssistant && message.shopifyBriefingCard && (
        <ShopifyBriefingCard
          card={message.shopifyBriefingCard}
          conversationId={conversationId}
          disabled={canSendToChat === false || shopifyBriefingActionable === false}
          onSend={onKickOffGoal ? text => onKickOffGoal(message, text) : undefined}
        />
      )}
      {isAssistant && message.postConnectedConnectorCard && (
        // The frame's own 680 (Figma 18942-127886), flush with the row like the band card it stacks under
        // after a band connect. The reading-column inset the connect card takes would squeeze it to 640
        // in the expanded chat, and leave its edge 40px off the band card's.
        <div style={{ boxSizing: 'border-box', width: '100%', maxWidth: 680 }}>
          <PostConnectedConnectorCard card={message.postConnectedConnectorCard} />
        </div>
      )}
      {/* "Running low on credits" card — rides the reply, so the assistant's answer is never replaced
          by the ask. Dismissing hides it for this thread only. */}
      {isAssistant && message.creditsCard && !creditsCardDismissed && (
        <CreditsCard card={message.creditsCard} onDismiss={() => setCreditsCardDismissed(true)} />
      )}
      {/* The owner's invite link at a good moment, and "Share this pack" on the turn that created one. Both
          ride the reply like the credits card above (the server never sends the invite beside it). */}
      {isAssistant && message.inviteCard && <InviteCard card={message.inviteCard} />}
      {isAssistant && message.packShareCard && <ChatPackShareCard card={message.packShareCard} />}
      {/* The outreach preview: what each contact will receive, drawn for its channel. The reply below
          it asks whether to send; the chips under that are its Send / Edit actions. */}
      {isAssistant && message.outreachPreviewCard && <OutreachPreviewCard card={message.outreachPreviewCard} />}
      {isAssistant && message.socialPostPreviewCard && (
        <>
          <SocialPostPreviewCard card={message.socialPostPreviewCard} />
          <SocialPostAutoApproveLine card={message.socialPostPreviewCard} />
          <SocialPostTaskState
            card={message.socialPostPreviewCard}
            answered={(selectedChoiceIds?.size ?? 0) > 0}
            canSend={canSendToChat !== false}
            onTap={onSendSocialPostTap ? (tap) => onSendSocialPostTap(message, tap) : undefined}
          />
        </>
      )}
      {isAssistant && message.emailCampaignPreviewCard && (
        <EmailCampaignPreviewCard
          card={message.emailCampaignPreviewCard}
          conversationId={threadConversationId}
          cardKey={message.id}
          latest={emailCardLatest === true}
          reviewMode={emailReviewMode ?? null}
          pastComments={cardReviewComments}
          canSend={canSendToChat !== false}
          sendComments={onSendEmailReview ? (text) => onSendEmailReview(message, text) : undefined}
        />
      )}
      {isAssistant && message.blogDraftPreviewCard && (
        <BlogDraftPreviewCard
          card={message.blogDraftPreviewCard}
          conversationId={threadConversationId}
          cardKey={message.id}
          latest={blogCardLatest === true}
          pastComments={cardReviewComments}
          canSend={canSendToChat !== false}
          sendComments={onSendBlogReview ? (text) => onSendBlogReview(message, text) : undefined}
        />
      )}
      {isAssistant && message.draftPreviewCard && (
        <DraftPreviewCard
          card={message.draftPreviewCard}
          conversationId={threadConversationId}
          cardKey={message.id}
          latest={draftCardLatest === true}
          held={isDraftActHeld(message.draftPreviewCard, message.choices)}
          pastComments={cardReviewComments}
          canSend={canSendToChat !== false}
          sendComments={onSendDraftReview ? (text, source, draftAction) => onSendDraftReview(message, text, source, draftAction) : undefined}
        />
      )}
      {/* The share offer: the card as the recipient meets it. It renders NO buttons — the names are this
          row's ordinary choice chips below, which is what lets an app that has never heard of the card
          key still send. See ShareOfferCard's own doc. */}
      {isAssistant && message.shareOfferCard && <ShareOfferCard card={message.shareOfferCard} />}
      <div
        style={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          // Figma Spacing/10 between the voice icon and the bubble (node 14511:160571).
          gap: 10,
          // Span the row so the bubble's width/max-width resolve against the full
          // row; without this the wrapper collapses and a short message wraps.
          width: '100%',
          maxWidth: '100%',
          // Full-screen desktop chat (Monday 12819056547 "[Desktop] Paddings", Figma
          // "Spacing/40"): both edges of the row carry a 40px inset from the reading
          // column, so the user bubble (flush to the row's own end) and the assistant
          // message (flush to the row's own start) land the SAME distance from the
          // column edges — that's what "aligned" means here, not zero-gap/flush.
          // Narrow/docked keep no inset (unchanged). No global border-box reset in this app, so
          // the row needs box-sizing set explicitly here — otherwise the inset padding would add
          // to the 100% width and overflow the 720px reading column by 80px.
          ...(alignEdges ? { boxSizing: 'border-box', paddingInlineStart: ROW_EDGE_INSET, paddingInlineEnd: ROW_EDGE_INSET } : {}),
        }}
      >
        {/* Voice marker: the DS mic icon button in the side gutter, centered on the first
            text line. Voice-CALL turns only — a voice NOTE's audio attachment renders
            the inline player instead. */}
        {isVoice && !hasAudioAttachment && (
          <span style={{ marginTop: isUser ? 3 : 0, display: 'inline-flex' }}>
            <MicGlyph title={isUser ? t('chat.voice.youSpoke') : t('chat.voice.saidOnCall')} />
          </span>
        )}
        <div
          style={
            isUser
              ? isVoice
                ? USER_BUBBLE_VOICE
                : typedBubble(USER_BUBBLE)
              : brief && attributedAgentId
              ? { ...AGENT_BUBBLE, borderColor: hexToRgba(colorForAgent(attributedAgentId), 0.5) }
              : ASSISTANT_BODY
          }
        >
          {groupRow?.forwarded && <GroupForwardedLabel />}
          {isUser && message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
          {/* Voice note: player first, bare transcript below it, slightly dimmed. */}
          {isVoice && message.attachments && message.attachments.length > 0 && (
            <SentAttachmentList
              attachments={message.attachments}
              tone={isUser ? 'bubble' : 'surface'}
              conversationId={threadConversationId}
              messageId={message.id}
              createdAt={message.createdDate}
            />
          )}
          {typedContent ? (
            typedBody(groupMaestro ? participantLabel(groupMaestro, t) : t('chat.group.you'))
          ) : isVoice ? (
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block', opacity: 0.72, ...(hasAudioAttachment ? { marginTop: 8 } : {}) }}>
              <LinkifiedText text={displayed} />
            </span>
          ) : isAssistant ? (
            <AssistantMarkdownContent
              displayed={
                // Phase 8: a Maestro asking another Maestro — the asked Maestros' `@Name`s read as mentions.
                groupMaestro
                  ? (() => {
                      const ask = maestroAskMentions(groupChat!.summary, message.author, message.groupMeta?.maestroMentions, displayed);
                      return mentionMarkdown(displayed, ask.names, ask.missing);
                    })()
                  : displayed
              }
              isCursorOn={!!isCursorOn}
            />
          ) : isUser && message.calendarItem ? (
            // The calendar item the owner tapped: its card stands in for the seed text the agent reads.
            <CalendarItemMessage card={message.calendarItem} />
          ) : isUser && message.displayText ? (
            // The prompt the agent reads with its ids swapped for names. A name is a customer's own text, so it is drawn
            // plain: no mention or markdown parsing.
            <span dir={messageDir(message.displayText)} style={{ display: 'block' }}>
              {message.displayText}
            </span>
          ) : sentReview ? (
            // The owner's finished site review: a compact card, not the URLs and selectors the agent reads.
            <SiteReviewMessage comments={sentReview} />
          ) : sentEmailReview ? (
            <EmailReviewMessage comments={sentEmailReview} />
          ) : sentBlogReview ? (
            <EmailReviewMessage comments={sentBlogReview} title={t('blogDraft.sent.title')} />
          ) : sentDraftReview ? (
            <EmailReviewMessage comments={sentDraftReview.comments} title={sentDraftReview.kind === 'BLOG_POST' ? t('blogDraft.sent.title') : undefined} />
          ) : (
            // display:block so dir="auto" also sets the line alignment — an inline span only
            // reorders bidi runs and leaves RTL text ragged-left inside the bubble.
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block' }}>
              {/* A picked contact's `[Name]` token renders as a bold `@Name` mention here. */}
              <ContactMentionText text={displayed} mentionNames={groupChat && displayed.includes('@') ? groupMentionNames(groupChat.summary) : undefined} />
            </span>
          )}
          {isAssistant && !isVoice && message.productCards && (
            <ProductCards card={message.productCards} onBuy={onBuyProduct && ((text) => onBuyProduct(message, text))} />
          )}
          {!isVoice && fileAttachments.length > 0 && (
            <SentAttachmentList
              attachments={fileAttachments}
              tone={isUser ? 'bubble' : 'surface'}
              conversationId={threadConversationId}
              messageId={message.id}
              createdAt={message.createdDate}
              wixelActions={
                isAssistant
                  ? {
                      onDesignReview: onRequestDesignReview && ((text) => onRequestDesignReview(message, text)),
                      canSend: canSendToChat !== false,
                    }
                  : undefined
              }
            />
          )}
          {/* User keeps its timestamp inside the bubble; the assistant's moves to
              the tool bar row below (right-aligned). The voice bubble sets the mock's
              12px text→time gap and Tiny/Medium -3% tracking (Figma 14511:160571). */}
          {isUser && time && !message.groupPost && (
            <div
              style={{
                ...TIME_BASE,
                color: 'var(--ds-text-secondary)',
                ...(isVoice ? { marginTop: 12, letterSpacing: '-0.03em' } : {}),
              }}
            >
              {groupRow && <GroupTimeBadges edited={groupRow.edited} starred={groupRow.starred} />}
              {time}
              {/* Design §21: ✓ / ✓✓ / blue ✓✓ beside the time on the viewer's own group row. */}
              {groupRow?.tick && <GroupTicks state={groupRow.tick} />}
            </div>
          )}
        </div>
      </div>
      {/* A group post this browser could not deliver: what went wrong, and the retry (same clientMessageId). */}
      {isUser && message.groupPost?.state === 'failed' && (
        <div data-testid="group-post-failed" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, marginTop: 4, paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0 }}>
          {message.groupPost.errorKey && message.groupPost.errorKey !== 'chat.group.error.generic' && (
            <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>{t(message.groupPost.errorKey)}</span>
          )}
          <TextButton size="small" color="destructive" onClick={onRetryGroupPost} disabled={!onRetryGroupPost}>
            {t('chat.group.sendFailed')}
          </TextButton>
        </div>
      )}
      {/* Phase 10b: the reactions on the viewer's own group post, under its bubble. */}
      {isUser && groupRow && <GroupReactionChips chips={groupRow.chips} summary={groupRow.summary} onChip={groupRow.onChip} overlapBubble inset={alignEdges ? ROW_EDGE_INSET : 0} />}
      {/* Phase 10a: the viewer's own group post — its ⋯ menu under the bubble (Phase 10b: and its react toggle). */}
      {isUser && (groupMenuActions?.length || groupRow?.onReact) ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 4, paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0 }}>
          {groupRow?.onReact && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} />}
          {groupMenuActions?.length ? <GroupRowMenu actions={groupMenuActions} /> : null}
        </div>
      ) : null}
      {isUser && groupRow?.onReact && reactOpen && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0 }}>
          <GroupQuickReactBar mine={groupRow.mine} onPick={(emoji) => { groupRow.onReact?.(emoji); setReactOpen(false); }}
            onMore={groupRow.onMoreReactions ? () => { groupRow.onMoreReactions?.(); setReactOpen(false); } : undefined} />
        </div>
      )}
      {/* A note the owner wrote from a thread's "Tell {agent}" composer: the chip names the conversation it
          is about and opens it. */}
      {isUser && message.outreachComposer && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <OutreachComposerChip marker={message.outreachComposer} />
        </div>
      )}
      {!holdBelow && (
      <Below>
      {/* The owner's Approve / Decline for a question a guest asked in a meeting — under the prose that
          asks it, in the same reading column. */}
      {isAssistant && meetingApprovalRow && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <MeetingApprovalCard row={meetingApprovalRow} />
        </div>
      )}
      {/* The proposed tasks this reply offers, one checkbox each — under the prose that offers them. */}
      {isAssistant && proposalRows && proposalRows.length > 0 && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <ProposalsCard rows={proposalRows} />
        </div>
      )}
      {/* Connector connect card — the reply text carries no URL, this card IS the connect
          affordance (arrives in-band at messageEnd, like choices). The one card that sits UNDER the
          words rather than above them: the words say what to connect and why, then the card hands over
          the button, then the chips offer the alternatives. Same order on iOS and Android. */}
      {isAssistant && message.connectorCard && (
        // The card renders OUTSIDE the reply's own inset body, so confine it to the SAME box the prose
        // uses (`ASSISTANT_BODY`: maxWidth 720, plus the fullscreen `alignEdges` 40px reading-column inset)
        // — otherwise the full-width card overflows the text column on both edges in the expanded chat.
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <ConnectorConnectCard
            card={message.connectorCard}
            messageId={message.finalId ?? message.id}
            onRequestNewLink={onRequestConnectorLink ? (text) => onRequestConnectorLink(message, text) : undefined}
            onConnected={onConnectorConnected}
            canSend={canSendToChat}
          />
        </div>
      )}
      {/* "Your Symphony card" — the profile link this turn claimed, drafted or changed, under the words that say so,
          in the connector card's slot: the words explain, the card hands over Open / Share / Edit. Same order on iOS and Android. */}
      {isAssistant && message.profileLinkCard && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <ProfileLinkChatCard card={latestProfileLinkCard ?? message.profileLinkCard} messageId={message.finalId ?? message.id} readOnly={profileLinkCardReadOnly} />
        </div>
      )}
      {/* The goal card sits UNDER the words: it answers the question the reply asks ("ready to set the team
          to work?") and on the completion turn follows the wrap-up it celebrates. */}
      {isAssistant && message.goalCard && (
        <ChatGoalCard
          card={message.goalCard}
          conversationId={conversationId}
          onKickOff={onKickOffGoal ? (text) => onKickOffGoal(message, text) : undefined}
          canSend={canSendToChat !== false}
        />
      )}
      {/* The messages this turn left WAITING (the send rule), each with its own Cancel. Drawn from this row, so
          a newer reply never closes it, and NOT through `onSelectChoice`: Cancel is a direct call, never a chat
          send, and is never disabled by a streaming reply, a locked channel or credits. */}
      {isAssistant && message.sendHoldCard && <HeldSendChips card={message.sendHoldCard} conversationId={conversationId} />}
      {/* The meeting summary's waiting messages (meeting hold): the same rows with Cancel and Send now, live BY HOLD ID —
          never by this row's conversation, which is not where a meeting hold lives. */}
      {isAssistant && message.meetingHoldsCard && <HeldSendChips card={message.meetingHoldsCard} meeting />}
      {isAssistant && message.meetingReconnect && <MeetingReconnectButton />}
      {isAssistant && message.choices && (
        <ChoiceChips
          choices={message.choices}
          selectedIds={selectedChoiceIds ?? EMPTY_SELECTED_IDS}
          onSelect={onSelectChoice ? (option) => onSelectChoice(message, option) : undefined}
          afterFirst={
            message.socialPostPreviewCard && onSelectChoice && !message.choices.options.some((option) => /edit/i.test(option.id)) ? (
              <SocialPostEditFirstChip
                card={message.socialPostPreviewCard}
                answered={(selectedChoiceIds?.size ?? 0) > 0}
                canSend={canSendToChat !== false}
                onTap={onSendSocialPostTap ? (tap) => onSendSocialPostTap(message, tap) : undefined}
              />
            ) : undefined
          }
        />
      )}
      {/* Below the name chips, as on both phones. No handler on a channel-locked thread → no control. */}
      {isAssistant && message.shareOfferCard?.someoneElse && !message.shareOfferCard.previewOnly && onShareSomeoneElse && (
        <ShareSomeoneElse
          card={message.shareOfferCard}
          onSend={(recipient) => onShareSomeoneElse(message, recipient)}
          onOpened={onShareSomeoneElseOpened ? () => onShareSomeoneElseOpened(message) : undefined}
          disabled={shareSomeoneElseDisabled}
        />
      )}
      {/* The transcript keeps the RECORD of what was asked — the prompts alone. The answerable card
          itself stands in the COMPOSER's place (MessagesPanel), so an ask cannot be both a thing you
          scroll away from and the thing you are being asked right now. Passing no `onSubmit` is what
          settles it. */}
      {isAssistant && message.survey && <SurveyWidget survey={message.survey} onReopen={onReopenSurvey} />}
      <ReactionChips
        reactions={message.reactions}
        onToggle={toggleReaction}
        inset={alignEdges ? ROW_EDGE_INSET : 0}
        overlapBubble={hasBubble}
      />
      {/* Phase 10b: a Maestro's group reply carries the group's reactions (its own row's are drawn above). */}
      {isAssistant && groupRow && <GroupReactionChips chips={groupRow.chips} summary={groupRow.summary} onChip={groupRow.onChip} inset={alignEdges ? ROW_EDGE_INSET : 0} />}
      {/* A brief turn keeps the clean round-table look (Figma "Morning Brief"): just the timestamp,
          left-aligned directly below the message — no copy/reply/react tool bar. */}
      {hideToolbar ? null : isAssistant && brief
        ? time && (
            // 12 below the bubble, matching the 12 above it (Figma "Morning Brief" itemSpacing 12).
            <div style={{ ...TIME_BASE, marginTop: 12, textAlign: 'start', color: 'var(--ds-text-secondary)' }}>{time}</div>
          )
        : /* Assistant tool bar: inline reply · copy · react buttons on the left (gap 4), timestamp on
             the right, 12px below the message — always visible, as the mock shows it (a user's own
             message has no tool bar). Reply and Copy are their own icon buttons, not a "more" menu
             (Figma 14692:215825). Clicking react opens the quick-react emoji bar as a second row below. */
          isAssistant &&
          (time || showCopy || onReply || toggleReaction || !!groupMenuActions?.length || !!groupRow?.onReact) && (
            <>
              <div
                style={
                  alignEdges
                    ? {
                        ...TOOLBAR_ROW,
                        boxSizing: 'border-box',
                        paddingInlineStart: ROW_EDGE_INSET,
                        paddingInlineEnd: ROW_EDGE_INSET,
                      }
                    : TOOLBAR_ROW
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
                  {showCopy && !groupRow && <CopyActionButton copySource={content} />}
                  {(toggleReaction || groupRow?.onReact) && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} onPick={pickReaction} onClose={closeReact} />}
                  {groupMenuActions?.length ? <GroupRowMenu actions={groupMenuActions} /> : null}
                </div>
                {time && <div style={{ ...TIME_BASE, marginTop: 0, color: 'var(--ds-text-secondary)' }}>{groupRow && <GroupTimeBadges edited={groupRow.edited} starred={groupRow.starred} />}{time}</div>}
              </div>
              {groupRow?.onReact && reactOpen && (
                <div style={{ marginTop: 12, ...(alignEdges ? { paddingInlineStart: ROW_EDGE_INSET, paddingInlineEnd: ROW_EDGE_INSET } : null) }}>
                  <GroupQuickReactBar mine={groupRow.mine} onPick={(emoji) => { groupRow.onReact?.(emoji); setReactOpen(false); }}
                    onMore={groupRow.onMoreReactions ? () => { groupRow.onMoreReactions?.(); setReactOpen(false); } : undefined} />
                </div>
              )}
            </>
          )}
      </Below>
      )}
    </div>
  );
}

/** Whether the row draws a card ABOVE its own words — the order the render below uses. */
function hasCardAboveWords(m: Message): boolean {
  return !!(
    m.insightCard ||
    m.connectorResultCard ||
    m.opportunityCard ||
    m.bandOfferCard ||
    m.bandCard ||
    m.postConnectedConnectorCard ||
    m.shopifyBatchCard ||
    m.shopifyBriefingCard ||
    m.creditsCard ||
    m.inviteCard ||
    m.packShareCard ||
    m.outreachPreviewCard ||
    m.socialPostPreviewCard ||
    m.emailCampaignPreviewCard ||
    m.blogDraftPreviewCard ||
    m.draftPreviewCard ||
    m.shareOfferCard
  );
}

/** A staged row's under-the-words blocks, arriving together once the words are typed. */
function ArriveGroup({ children }: { children: ReactNode }) {
  return <div className={styles.arriveGroup}>{children}</div>;
}

// Brief speaker avatar — 28px (Figma "Morning Brief"), a touch larger than the 24px WA-group avatar.
const BRIEF_AVATAR = 28;

// Full-screen desktop chat's inline inset off the reading column edges (Figma "Spacing/40",
// Monday 12819056547 "[Desktop] Paddings" — a follow-up correction to the original alignEdges
// pass, which had dropped the assistant body's trailing inset to 0 instead of this value).
const ROW_EDGE_INSET = 40;

const TOOLBAR_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  // Figma "Full screen / Chat Layout" QA (Monday 12819150619): 12px between the answer and its
  // reply/copy/react toolbar — was 8. This is the bond `rowGapAfter` keeps the row's own gap clear of.
  marginTop: TRAILING_BLOCK_GAP,
  width: '100%',
  maxWidth: 720,
};
